import React, { useState } from "react";
import { CaretLeftIcon, CaretRightIcon, DownloadSimpleIcon } from "@phosphor-icons/react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n) => String(n).padStart(2, "0");
const fmt = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

const STATUS_CHIP = {
  scheduled: "bg-gold-100 text-gold-800 hover:bg-gold-200",
  "in-progress": "bg-blue-100 text-blue-800 hover:bg-blue-200",
  completed: "bg-green-100 text-green-800 hover:bg-green-200",
  new: "bg-slate-100 text-slate-700 hover:bg-slate-200",
};
const chipClass = (s) => STATUS_CHIP[s] || STATUS_CHIP.scheduled;

// jobs: [{ id, date: 'YYYY-MM-DD', title, status }]
export const MonthCalendar = ({ jobs = [], onJobClick, draggable = false, onReschedule, exportable = false }) => {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [draggedJob, setDraggedJob] = useState(null);
  const [dragOverDate, setDragOverDate] = useState(null);
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstWeekday = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const byDate = {};
  jobs.forEach((j) => {
    if (!j.date) return;
    (byDate[j.date] = byDate[j.date] || []).push(j);
  });

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = fmt(today.getFullYear(), today.getMonth(), today.getDate());
  const monthPrefix = `${year}-${pad(month + 1)}`;
  const monthCount = jobs.filter((j) => (j.date || "").startsWith(monthPrefix)).length;

  const exportMonthCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = jobs
      .filter((j) => (j.date || "").startsWith(monthPrefix))
      .sort((a, b) => (a.date > b.date ? 1 : -1));
    const header = ["Date", "Reference", "Customer", "Phone", "Service", "Status"];
    const lines = [header.join(",")];
    rows.forEach((j) => {
      lines.push([
        j.date,
        j.ref || j.title || "",
        j.name || j.customer || "",
        j.phone || "",
        Array.isArray(j.categories) ? j.categories.join("; ") : (j.category || ""),
        j.status || "",
      ].map(esc).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mcc-schedule-${monthPrefix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="month-calendar" className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="flex items-center justify-between mb-4">
        <button
          data-testid="calendar-prev-month"
          onClick={() => setCursor(new Date(year, month - 1, 1))}
          className="w-8 h-8 rounded-sm flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          aria-label="Previous month"
        >
          <CaretLeftIcon size={18} weight="bold" />
        </button>
        <div className="text-center">
          <p className="font-heading font-bold text-slate-900" data-testid="calendar-month-label">{MONTHS[month]} {year}</p>
          <p className="text-[11px] text-slate-400">{monthCount} scheduled {monthCount === 1 ? "job" : "jobs"}</p>
        </div>
        <button
          data-testid="calendar-next-month"
          onClick={() => setCursor(new Date(year, month + 1, 1))}
          className="w-8 h-8 rounded-sm flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
          aria-label="Next month"
        >
          <CaretRightIcon size={18} weight="bold" />
        </button>
      </div>

      {exportable && monthCount > 0 && (
        <div className="flex justify-end mb-2">
          <button
            data-testid="calendar-export-btn"
            onClick={exportMonthCsv}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-sm px-2.5 py-1 transition-colors"
          >
            <DownloadSimpleIcon size={14} /> Download this month
          </button>
        </div>
      )}

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-center text-[11px] font-bold uppercase tracking-wide text-slate-400 py-1">{w}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="min-h-[68px] rounded-sm bg-slate-50/40" />;
          const ds = fmt(year, month, d);
          const dayJobs = byDate[ds] || [];
          const isToday = ds === todayStr;
          const isDropTarget = draggable && dragOverDate === ds;
          return (
            <div
              key={i}
              data-testid={`calendar-day-${ds}`}
              onDragOver={draggable ? (e) => { e.preventDefault(); setDragOverDate(ds); } : undefined}
              onDragLeave={draggable ? () => setDragOverDate((cur) => (cur === ds ? null : cur)) : undefined}
              onDrop={draggable ? (e) => {
                e.preventDefault();
                setDragOverDate(null);
                if (draggedJob && draggedJob.date !== ds) onReschedule?.(draggedJob, ds);
                setDraggedJob(null);
              } : undefined}
              className={`min-h-[68px] rounded-sm border p-1 transition-colors ${isDropTarget ? "border-gold-500 border-dashed bg-gold-50 ring-1 ring-gold-400" : isToday ? "border-gold-500 bg-gold-50/50" : "border-slate-100 hover:border-slate-200"}`}
            >
              <p className={`text-[11px] font-semibold mb-0.5 ${isToday ? "text-gold-700" : "text-slate-500"}`}>{d}</p>
              <div className="space-y-0.5">
                {dayJobs.slice(0, 3).map((j, k) => (
                  <button
                    key={k}
                    data-testid={`calendar-job-${j.id}`}
                    draggable={draggable}
                    onDragStart={draggable ? (e) => { setDraggedJob(j); e.dataTransfer.effectAllowed = "move"; } : undefined}
                    onDragEnd={draggable ? () => { setDraggedJob(null); setDragOverDate(null); } : undefined}
                    onClick={() => onJobClick?.(j)}
                    title={draggable ? `${j.title} — drag to reschedule` : j.title}
                    className={`w-full flex items-center gap-1 text-left rounded-[3px] px-1 py-0.5 text-[10px] font-semibold transition-colors active:scale-95 ${chipClass(j.status)} ${draggable ? "cursor-move" : ""}`}
                  >
                    {j.thumb && (
                      <img src={j.thumb} alt="" draggable={false} className="w-3.5 h-3.5 rounded-[2px] object-cover flex-shrink-0 border border-gold-300" />
                    )}
                    <span className="truncate">{j.title}</span>
                  </button>
                ))}
                {dayJobs.length > 3 && (
                  <p className="text-[9px] text-slate-400 px-1">+{dayJobs.length - 3} more</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
